import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconModule } from '@vinnova/ui';
import { AnalyticsEvent, AnalyticsTrackingService } from '@vinnova/utilities';
import { marked } from 'marked';
import { ChatMessage } from '../core/models/chatbot.model';
import { ChatbotService } from '../core/services/chatbot.service';

@UntilDestroy()
@Component({
  selector: 'vinnova-chatbot-widget',
  standalone: true,
  imports: [FormsModule, IconModule, TranslocoModule],
  templateUrl: './chatbot-widget.component.html',
  styleUrls: ['./chatbot-widget.component.scss'],
})
export class ChatbotWidgetComponent implements OnInit {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('messageInputElement', { static: false })
  private messageInputElement!: ElementRef;
  @ViewChild('chatToggleButton', { static: false })
  private chatToggleButton!: ElementRef;

  isOpen = false;
  messages: ChatMessage[] = [];
  isTyping = false;
  messageInput = '';
  personName = '';
  autoFocus = false;
  hasSentFirstMessage = false;
  private isMouseDown = false;
  private savedScrollY = 0;

  private get isMobile(): boolean {
    return globalThis.matchMedia('(max-width: 576px)').matches;
  }

  constructor(
    public chatbotService: ChatbotService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private analyticsTrackingService: AnalyticsTrackingService,
  ) {}

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event): void {
    if (this.isOpen) {
      event.preventDefault();
      this.chatbotService.toggle();
    }
  }

  // översätt newlines till <br>
  convertNewlinesToBr(text: string): string {
    return text.replaceAll(String.raw`\n`, '<br>');
  }

  // behöver hämtas i en enskild string för att inte se konstig ut
  getGreeting(t: (key: string) => string): string {
    return `${t('chatbot_hello')} ${this.personName}! 👋`;
  }

  ngOnInit(): void {
    this.chatbotService.personName$
      .pipe(untilDestroyed(this))
      .subscribe((name) => {
        this.personName = name;
      });

    this.chatbotService.isOpen$
      .pipe(untilDestroyed(this))
      .subscribe((isOpen) => {
        this.isOpen = isOpen;
        this.updateChatOpenCssClass();
      });
    this.chatbotService.messages$
      .pipe(untilDestroyed(this))
      .subscribe((messages) => {
        this.messages = messages.map((msg) => {
          let contentToSanitize = msg.content;

          // Konvertera markdown till HTML för bot-meddelanden
          if (msg.sender === 'bot' && msg.messageType === 'normal') {
            contentToSanitize = marked.parse(msg.content, {
              async: false,
            }) as string;
          }

          // Sanera innehållet för att förhindra XSS
          const sanitized = this.sanitizer.sanitize(1, contentToSanitize);
          if (sanitized) {
            msg.sanitizedContent =
              this.sanitizer.bypassSecurityTrustHtml(sanitized);
          }

          return msg;
        });
      });

    this.chatbotService.isTyping$
      .pipe(untilDestroyed(this))
      .subscribe((isTyping) => {
        this.isTyping = isTyping;
        if (!isTyping && this.messageInputElement) {
          setTimeout(() => {
            this.autoFocus = true;
            const inputElement = this.messageInputElement.nativeElement;
            inputElement.focus();
          }, 0);
        }
      });
  }

  toggleChat(): void {
    this.chatbotService.toggle();
    setTimeout(() => {
      this.analyticsTrackingService.event(AnalyticsEvent.CHAT_TOGGLE, {
        state: this.isOpen ? 'open' : 'closed',
      });
      if (this.isOpen) {
        const closeBtn = this.elementRef.nativeElement.querySelector(
          '.close-btn',
        ) as HTMLElement;
        if (closeBtn) {
          closeBtn.focus();
        }

        if (this.messageInputElement) {
          this.calculateMarginBottom();
          this.scrollToBottom();
          const div = this.messageInputElement.nativeElement;
          if (this.messageInput && !div.textContent) {
            div.textContent = this.messageInput;
          }
        }
      } else {
        if (this.chatToggleButton) {
          this.chatToggleButton.nativeElement.focus();
        }
      }
    }, 0); // Wait for DOM to update
  }

  sendMessage(): void {
    if (!this.messageInput.trim()) return;

    const message = this.messageInput.trim();
    this.messageInput = '';

    if (this.messageInputElement) {
      const div = this.messageInputElement.nativeElement;
      div.textContent = '';
    }
    if (!this.hasSentFirstMessage) {
      this.analyticsTrackingService.event(
        AnalyticsEvent.FIRST_CHAT_MESSAGE_SENT,
      );
      this.hasSentFirstMessage = true;
    }

    this.chatbotService.sendUserMessage(message);
    this.scrollToBottom();
    this.chatbotService
      .sendBotMessage(message)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // När bot-svaret är klart, animera scrollning
        this.animateScrollToBotResponse();
      });
  }

  private updateChatOpenCssClass(): void {
    const widgetElement =
      this.elementRef.nativeElement.querySelector('.chatbot-widget');

    if (widgetElement) {
      if (this.isOpen) {
        this.renderer.addClass(widgetElement, 'chatbot-open');
      } else {
        this.renderer.removeClass(widgetElement, 'chatbot-open');
      }
    }

    if (this.isMobile) {
      if (this.isOpen) {
        this.savedScrollY = window.scrollY;
        this.renderer.setStyle(document.body, 'position', 'fixed');
        this.renderer.setStyle(document.body, 'top', `-${this.savedScrollY}px`);
        this.renderer.setStyle(document.body, 'left', '0');
        this.renderer.setStyle(document.body, 'right', '0');
      } else {
        this.renderer.removeStyle(document.body, 'position');
        this.renderer.removeStyle(document.body, 'top');
        this.renderer.removeStyle(document.body, 'left');
        this.renderer.removeStyle(document.body, 'right');
        window.scrollTo(0, this.savedScrollY);
      }
    }
  }

  // scrolla ner automatiskt till botten av chatten
  private scrollToBottom(): void {
    if (!this.messagesContainer) {
      return;
    }
    setTimeout(() => {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }, 0);
  }

  // Smooth scroll animation för bot-svar
  private animateScrollToBotResponse(): void {
    if (!this.messagesContainer) return;

    // Vänta 600ms
    setTimeout(() => {
      const container = this.messagesContainer.nativeElement;
      const messages = container.querySelectorAll('.message');
      if (messages.length < 2) return;

      // eslint-disable-next-line unicorn/prefer-at
      const userMessage = messages[messages.length - 2] as HTMLElement;
      const startPos = container.scrollTop;
      // Justera så hela user-meddelandet syns (offsetTop pekar på toppen av elementet)
      const targetPos = Math.max(0, userMessage.offsetTop - 48);
      const distance = targetPos - startPos;
      const duration = 900;
      let start: number;

      const step = (timestamp: number) => {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-in-out formel
        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        container.scrollTop = startPos + distance * ease;

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }, 600);
  }

  onInput(event: Event): void {
    const div = event.target as HTMLDivElement;
    this.messageInput = div.textContent || '';

    this.updateSendButtonPosition();
    // Ta bort placeholder om det finns text
    if (this.messageInput.trim()) {
      delete div.dataset['placeholder'];
    } else {
      // Om input blir tom, sätt placeholder
      div.textContent = '';
      this.messageInput = '';
      div.dataset['placeholder'] = 'Skriv ditt meddelande...';
    }
  }

  // Tryck enter för att skicka men håll in skift + enter för radbryt
  onEnterKey(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onMouseDown(): void {
    this.isMouseDown = true;
  }

  onSendButtonMouseDown(event: MouseEvent): void {
    // Förhindra att send knapp får fokus vid klick
    event.preventDefault();
  }

  onFocus(): void {
    if (!this.isMouseDown && this.messageInputElement && !this.autoFocus) {
      // Keyboard focus (Tab)
      this.renderer.addClass(
        this.messageInputElement.nativeElement,
        'keyboard-focus',
      );
    } else if (this.autoFocus) {
      this.autoFocus = false;
      this.isMouseDown = true;
    }
    this.isMouseDown = false;
  }

  onBlur(): void {
    if (this.messageInputElement) {
      this.renderer.removeClass(
        this.messageInputElement.nativeElement,
        'keyboard-focus',
      );
    }
  }

  // Ändra max-höjd beroende på fönster-höjd
  private calculateMarginBottom(): void {
    // Skippa detta på mobil
    if (this.isMobile) {
      return;
    }

    const chatbotWindow =
      this.elementRef.nativeElement.querySelector('.chatbot-window');

    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - 20 - 70;
    const maxHeight = Math.max(200, availableHeight);

    if (chatbotWindow) {
      this.renderer.setStyle(chatbotWindow, 'max-height', `${maxHeight}px`);
    }
  }

  private updateSendButtonPosition(): void {
    setTimeout(() => {
      const inputElement = this.messageInputElement.nativeElement;
      const hasScrollbar =
        inputElement.scrollHeight > inputElement.clientHeight;

      inputElement.classList.toggle('overflow-auto', hasScrollbar);
    }, 0);
  }
}
